import type { IColor } from '@color';
import { Point, Rect, Size } from '@geom';
import { immerable } from '@utils';

import { expectDefined } from '@utils';
import type { BorderElementConfig } from './BorderElementConfig';
import {
  type CornerRadius,
  type ElementId,
  type ImageElementData,
  LayoutConfig,
  type TextElementData
} from './types';

export class LayoutElement {
  [immerable] = true;

  _origin = Point.ZERO;
  _dimensions = Size.ZERO;
  _id: ElementId | undefined;
  layoutConfig: LayoutConfig = new LayoutConfig();
  backgroundColor: IColor | undefined;
  cornerRadius: CornerRadius = {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  };
  border: BorderElementConfig | undefined;
  textData?: TextElementData;
  imageData?: ImageElementData;
  parentId: ElementId | undefined;
  childIds: ElementId[] = [];
  hovered = false;
  selectionHaloOpacity = 0;
  copyOverlayOpacity = 0;
  opacity = 1;
  clip = false;
  zIndex = 0;

  get origin(): Point {
    return this._origin;
  }

  set origin(value: Point) {
    if (!this._origin.equals(value)) {
      // console.log(`Origin changed from ${this._origin.toString()} to ${value.toString()}`);
      this._origin = value;
    }
  }

  get dimensions(): Size {
    return this._dimensions;
  }

  set dimensions(value: Size) {
    if (!this._dimensions.equals(value)) {
      // console.log(`Dimensions changed from ${this._dimensions.toString()} to ${value.toString()}`);
      this._dimensions = value;
    }
  }

  // Setter methods for maintaining relationships
  setParent(parentId: ElementId | undefined): void {
    this.parentId = parentId;
  }

  addChild(childId: ElementId): void {
    if (!this.childIds.includes(childId)) {
      this.childIds.push(childId);
    }
  }

  removeChild(childId: ElementId): void {
    const index = this.childIds.indexOf(childId);
    if (index !== -1) {
      this.childIds.splice(index, 1);
    }
  }

  get selectionHalo(): boolean {
    return this.selectionHaloOpacity > 0;
  }

  set selectionHalo(value: boolean) {
    this.selectionHaloOpacity = value ? 1 : 0;
  }

  get copyOverlay(): boolean {
    return this.copyOverlayOpacity > 0;
  }

  set copyOverlay(value: boolean) {
    this.copyOverlayOpacity = value ? 0.5 : 0;
  }

  get id(): ElementId {
    return expectDefined(this._id);
  }

  set id(value: ElementId) {
    if (this._id !== undefined && this._id !== value) {
      throw new Error(`Cannot change id from ${this._id} to ${value}`);
    }
    this._id = value;
  }

  /**
   * Change the id of this element, even if it already has one.
   * Use with caution - normally ids should not change after being set.
   * This is primarily for special cases like transition animations.
   */
  setIdUnchecked(value: ElementId): void {
    this._id = value;
  }

  get bounds(): Rect {
    return new Rect({
      x: this.origin.x,
      y: this.origin.y,
      width: this.dimensions.width,
      height: this.dimensions.height
    });
  }

  set bounds(value: Rect) {
    this.origin = value.topLeft;
    this.dimensions = value.size;
  }

  get isText(): boolean {
    return this.textData !== undefined;
  }

  get isImage(): boolean {
    return this.imageData !== undefined;
  }
}
